import styles from './ccf-scripted-copilot.module.scss';
import Box from '@mui/material/Box';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AdaptiveCard } from 'adaptivecards-react';
import * as ACData from 'adaptivecards-templating';
import ccfCopilotCardStyles from '../ccf-live-copilot.styles';
import { customCopilotVoiceTemplate } from '../ccf-copilot-chat/mock-data/copilot-voice-custom-flow';
import {
  CcfCopilotAutoSummary,
  CcfCopilotVideoCallCard,
  activeContactSelector,
  dispositionInteractionActions,
  getActiveContactID,
  getActiveInteractionID,
  getCopilotCardListForVideoCall,
  getIsEmailResponseLoadedInEditor,
  getIsDispositionOpen,
  getLoadAutoSummaryState,
  getShowSummaryLoadingAnimation,
  getSpaceBtnCounterForCopilotCard,
  getSpaceBtnCounterForCopilotEmail,
  getSpaceBtnCounterForCopilotVoice,
  getcopilotFlowMsgIndex,
  selectInboxCollapsedState,
  setIsCopilotEmailResponseGenerated,
  setIsDamagedLuggagePhotoUploaded,
  setIsReimbursementClaimApproved,
  setIsSuggestionClicked,
  setShowSummaryLoadingAnimation,
  suggestionsCard,
  updateAppSpaceTab,
  updateContactInteraction,
  updateCopilotCardListForVideoCall,
  updateLoadAutoSummary,
  updateSpaceBtnCounterForCopilotCard,
  updateSpaceBtnCounterForCopilotEmail,
  updateSpaceBtnCounterForCopilotVoice,
  getIsCopilotEmailResponseGenerated,
} from '@nice-ccf/ui-components';
import {
  CopilotElement,
  getAdaptiveCards,
  getIsRequestProcessing,
  selectRunningSummary,
  getIsJourneySummaryExpanded,
  getJourneySummaryAdaptiveCard,
  setIsRequestProcessing,
} from '../ccf-copilot-chat/+state/ccf-live-copilot.slice';
import {
  CcfAutosummaryAnimationIcon,
  CcfCard,
  CcfCopilotSuggestionIcon,
  CcfDancingDotsIcon,
  CcfImageWithZoom,
  CcfTypography,
  useTranslator,
} from '@nice-ccf/ui-controls';
import { CcfBox } from '@nice-ccf/ui-controls';
import CcfAgentCopilotRunningSummary from '../ccf-agent-copilot-running-summary';
import CcfAgentCopilotSearchContainer from '../ccf-agent-copilot-search-container';
import CcfEmptyCopilotState from '../ccf-empty-copilot-state';
import {
  ActionMetaData,
  CardActions,
  CardData,
  CcfAgentCopilotContainerFunctions,
  ContainerId,
  actionsForOpenContainer,
} from '../ccf-live-copilot-helper';
import {
  handleJourneySummaryData,
  handleSubmitIntentMessages,
  processApiResponse,
} from '../ccf-copilot-util';
import { customCopilotTemplate } from '../ccf-copilot-chat/mock-data/custom-flow';
import { DigitalChannelType, MediaType } from '@nice-ccf/core-sdk';
import { ApiResponse } from '../ccf-copilot-api';
import { AppSpace } from 'libs/react/ui-components/src/enums/app-space-tabs';
import { customCopilotEmailTemplate } from '../ccf-copilot-chat/mock-data/copilot-email-custom-flow';
import { useMsal } from '@azure/msal-react';
import {
  copilotCardMockDataForVideoCall,
  damagedLuggageAutoSummaryData,
} from '../mock-data/copilot-mock-data';
import { getInteractionData } from '../../../ccf-interaction-workspace/ccf-interaction-mock-data';
import CcfProcessAutomationPanel, {
  isProcessAutomationEnabled,
} from '../../ccf-process-automation/ccf-process-automation-panel';
import { useTheme } from '@mui/material';

/**
 * Extracts time from interaction date time.
 * @param interactions - The interactions to be formatted.
 * @returns The formatted string.
 * @example extractTimeFromInteractionDateTime(interactions)
 */
const extractTimeFromInteractionDateTime = (perviousInteractionData: any) => {
  const modifiedData: any = perviousInteractionData.map((interaction: any) => {
    const interactionDateTime: Date = new Date(interaction.InteractionDateTime);
    const interactionTime: string = interactionDateTime
      .toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      .replace(/(\d+:\d+):\d+/, '$1');
    const interactionDate: string = interactionDateTime
      .toLocaleDateString()
      .slice(0, -5);
    return {
      ...interaction,
      InteractionTime: interactionTime,
      InteractionDate: interactionDate,
    };
  });
  return modifiedData;
};

/* eslint-disable-next-line */
export interface CcfScriptedCopilotProps {}

/**
 * Renders the CcfScriptedCopilot component.
 * @param props - The component props.
 * @example <CcfScriptedCopilot />
 * @returns The rendered CcfScriptedCopilot component.
 */
export function CcfScriptedCopilot(props: CcfScriptedCopilotProps) {
  const [translate] = useTranslator();
  const theme = useTheme();
  const { accounts } = useMsal();
  const loginCredentialsFromSSO = accounts[0]?.name || ' ';
  const loginCredentialsArray = loginCredentialsFromSSO?.split(' ');
  const activeContactId = useSelector(getActiveContactID);
  const dispatch = useDispatch();
  const activeContact = useSelector(activeContactSelector);
  let copilotFlowJson =
    activeContact?.customerChatReplyJsonData?.length > 0
      ? activeContact?.customerChatReplyJsonData
      : activeContact?.media === MediaType.VOICE
      ? customCopilotVoiceTemplate?.copilotAgentAndCustomerVoiceData
      : activeContact?.channel === DigitalChannelType.CHAT
      ? customCopilotTemplate?.copilotAgentAndCustomerChatData
      : customCopilotEmailTemplate?.copilotAgentAndCustomerEmailData;
  copilotFlowJson = copilotFlowJson.filter(
    (item: any) => item.messageFrom !== 'elevatedEmail'
  );
  copilotFlowJson = copilotFlowJson.filter(
    (item: any) => item.messageFrom !== 'elevatedSMS'
  );
  const spaceBtnClickedCountForVoice = useSelector(
    getSpaceBtnCounterForCopilotVoice
  );
  const spaceBtnClickedCountForEmail = useSelector(
    getSpaceBtnCounterForCopilotEmail
  );
  const isEmailResponseLoadedInEditor = useSelector(
    getIsEmailResponseLoadedInEditor
  );
  const isInboxCollapsed = useSelector(selectInboxCollapsedState);
  const isRequestProcessing = useSelector(getIsRequestProcessing);
  const [isResponseLoading, setResponseLoading] = useState(isRequestProcessing);
  const cardStyles = ccfCopilotCardStyles(theme);
  const isDispositionOpen = useSelector(getIsDispositionOpen);
  const { toggleDispositionCard } = dispositionInteractionActions;
  const updateCopilotCardSet: any = [0, 1, 2, 3, 5, 7, 8, 9, 10];
  const showSummaryLoadingAnimation = useSelector(
    getShowSummaryLoadingAnimation
  );
  const hostConfig = {
    fontFamily: theme.typography.fontFamily,
    separator: {
      lineThickness: 1,
      lineColor: theme?.palette?.background?.noteInput,
    },
    supportsInteractivity: true,
    containerStyles: {
      default: {
        foregroundColors: {
          default: {
            default: theme?.palette?.secondary?.main,
            subtle: theme?.palette?.secondary?.main,
          },
          dark: {
            default: theme?.palette?.text?.contrastText,
          },
        },
      },
    },
  };
  const isChannelVoice = activeContact?.media === 'Voice' ? true : false;
  const containerRef = useRef<HTMLDivElement | null>(null);
  const existingAdaptiveCards = useSelector(
    getAdaptiveCards(activeContactId || '')
  );
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const handleKeyDownRef = useRef<(event?: any) => void>(() => {});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const rtSummary = useSelector(
    selectRunningSummary(activeContactId || '')
  ) as CopilotElement;
  let initialRtSummary = rtSummary?.content
    ? JSON.parse(rtSummary?.content as unknown as string)
    : ({} as CopilotElement);

  if (
    initialRtSummary &&
    initialRtSummary.summary &&
    activeContact?.customer?.firstName
  ) {
    const words = initialRtSummary.summary.split(' ');
    if (words.length > 0) {
      words[0] = activeContact.customer.firstName;
      initialRtSummary.summary = words.join(' ');
    }
  }
  //const rtSummary = (useSelector(selectRunningSummary('201251606723')) as CopilotElement);
  const rtSummaryContent = initialRtSummary
    ? initialRtSummary
    : ({} as CopilotElement);
  const flowMsgIndex = useSelector(getcopilotFlowMsgIndex);
  const suggestionsCardList = copilotFlowJson[spaceBtnClickedCountForVoice];
  const spaceBtnClickedCount: number = useSelector(
    getSpaceBtnCounterForCopilotCard
  );
  const userReplyChatData = activeContact?.customerChatReplyJsonData ?? [];
  //const userReplyChatData = useSelector(getCustomerChatReplyJsonData(activeContact?.id))?? [];
  const intents = activeContact?.intents ?? [];
  const lastindexVal = activeContact?.customerChatReplyLastindexVal ?? 0;
  const activeInteractionID: string = useSelector(getActiveInteractionID) || '';

  useEffect(() => {
    setResponseLoading(isRequestProcessing);
  }, [isRequestProcessing]);

  useEffect(() => {
    let timeoutId: any = null;
    if (isResponseLoading) {
      timeoutId = setTimeout(() => {
        setResponseLoading(false);
      }, 20000);
    }
    return () => clearTimeout(timeoutId);
  }, [isResponseLoading]);

  useEffect(() => {
    if (
      (rtSummary === null || rtSummary === undefined) &&
      activeContact?.channel === DigitalChannelType.EMAIL &&
      spaceBtnClickedCountForEmail === 0
    ) {
      let description = activeContact?.rtSummary;
      if (activeContact?.rtSummary && activeContact?.customer?.firstName) {
        const words = activeContact?.rtSummary.split(' ');
        if (words.length > 0) {
          words[0] = activeContact.customer.firstName;
          description = words.join(' ');
        }
      }
      const response = {
        agentAssistSource: 'Illuminate',
        contactId: `${activeContact?.id}`,
        utcTimestamp: new Date().toISOString(),
        acpAppElements: [
          {
            cardType: 'Text',
            contentType: 'rtSummary',
            // eslint-disable-next-line no-useless-escape
            content: JSON.stringify({
              title: 'rtSummary',
              summary: description,
            }),
            objectId: 'e15d4e75-39fa-4f9d-85a1-daffe6d16a40_rtSummary',
          },
        ],
        type: null,
      } as unknown as ApiResponse;
      processApiResponse(response, dispatch);
      dispatch(setIsCopilotEmailResponseGenerated(false));
    }
  }, []);

  /**
   * @example
   *
   * @returns boolean
   */
  const checkIfAdaptiveCardExits = (contentType: string) => {
    return existingAdaptiveCards.some(
      (card: CopilotElement) => card.contentType === contentType
    );
  };

  useEffect(() => {
    setTimeout(() => {
      if (
        activeContact?.journeyHistory &&
        flowMsgIndex < copilotFlowJson.length &&
        activeContact?.perviousInteractionJsonData?.interactions?.length > 0 &&
        existingAdaptiveCards[0]?.contentType !== 'journeySummary'
      ) {
        let description =
          activeContact?.perviousInteractionJsonData?.journeySummaryText;
        if (
          activeContact?.perviousInteractionJsonData?.journeySummaryText &&
          activeContact?.customer?.firstName
        ) {
          const words =
            activeContact?.perviousInteractionJsonData?.journeySummaryText.split(
              ' '
            );
          if (words.length > 0) {
            words[0] = activeContact.customer.firstName;
            description = words.join(' ');
          }
        }
        //dispatch(updateselectedContact({ 'perviousInteractionJsonData', copilotFlowJson[0].interactions }));
        const updatedPerviousInteractionData =
          extractTimeFromInteractionDateTime(
            activeContact?.perviousInteractionJsonData.interactions
          );
        handleJourneySummaryData(
          activeContact,
          description,
          updatedPerviousInteractionData,
          dispatch
        );
        //dispatch(updateFlowMsgIndex(flowMsgIndex + 1));
      }
      if (
        activeContact?.showtransferSummary &&
        !checkIfAdaptiveCardExits('agentTransfer')
      ) {
        let description = activeContact?.transferSummary;
        if (
          activeContact?.transferSummary &&
          activeContact?.customer?.firstName
        ) {
          const words = activeContact?.transferSummary.split(' ');
          if (words.length > 0) {
            words[0] = activeContact.customer.firstName;
            description = words.join(' ');
          }
        }
        const content = {
          title: translate('agentTransferSummary'),
          description: description ?? '',
        };
        const response = {
          agentAssistSource: 'Illuminate',
          contactId: `${activeContact?.id}`,
          utcTimestamp: '2024-04-23T04:27:23.455584Z',
          acpAppElements: [
            {
              cardType: 'AdaptiveCard',
              contentType: 'agentTransfer',
              // eslint-disable-next-line no-useless-escape
              content: content,
              objetId: 'e15d4e75-39fa-4f9d-85a1-daffe6d16a40_agentTransfer',
            },
          ],
          type: null,
        } as unknown as ApiResponse;
        processApiResponse(response, dispatch);
      }
      if (
        (rtSummary === null || rtSummary === undefined) &&
        activeContact?.media === MediaType.VOICE
      ) {
        let rtDescription = activeContact?.rtSummary;
        if (activeContact?.rtSummary && activeContact?.customer?.firstName) {
          const words = activeContact?.rtSummary.split(' ');
          if (words.length > 0) {
            words[0] = activeContact.customer.firstName;
            rtDescription = words.join(' ');
          }
        }
        const response2 = {
          agentAssistSource: 'Illuminate',
          contactId: `${activeContact?.id}`,
          utcTimestamp: new Date().toISOString(),
          acpAppElements: [
            {
              cardType: 'Text',
              contentType: 'rtSummary',
              // eslint-disable-next-line no-useless-escape
              content: JSON.stringify({
                title: 'rtSummary',
                summary: rtDescription,
              }),
              objectId: 'e15d4e75-39fa-4f9d-85a1-daffe6d16a40_rtSummary',
            },
          ],
          type: null,
        } as unknown as ApiResponse;
        processApiResponse(response2, dispatch);
        // dispatch(
        //   updateSpaceBtnCounterForCopilotVoice(spaceBtnClickedCountForVoice + 1)
        // );
      }
    }, 1000);
  }, []);

  const [imageExpand, setimageExpand] = useState({ url: '', altText: '' });
  const context: ACData.IEvaluationContext = {
    $root: {},
  };
  const isSmView = false;
  const loadAutoSummary = useSelector(getLoadAutoSummaryState);
  const suggestionsVoiceCallCardList: suggestionsCard[] = useSelector(
    getCopilotCardListForVideoCall
  );
  const isEmailResponseGenerated = useSelector(
    getIsCopilotEmailResponseGenerated
  );
  const isJourneySummaryExpanded = useSelector(
    getIsJourneySummaryExpanded(activeContactId || '')
  );
  const journeySummaryCard = useSelector(
    getJourneySummaryAdaptiveCard(activeContactId || '')
  );

  useEffect(() => {
    if (isRequestProcessing) {
      setResponseLoading(isRequestProcessing);
      const timeoutId = setTimeout(() => {
        setResponseLoading(false);
      }, 20000);
      return () => clearTimeout(timeoutId);
    }
    return;
  }, [isRequestProcessing]);

  useEffect(() => {
    if (loadAutoSummary) {
      dispatch(setShowSummaryLoadingAnimation(true));
      setTimeout(() => {
        dispatch(setShowSummaryLoadingAnimation(false)); //// stop Auto Summary Processing animation after 3 seconnds
      }, 3000);
    }
  }, [loadAutoSummary]);

  useEffect(() => {
    if (isEmailResponseLoadedInEditor && spaceBtnClickedCountForEmail > 0) {
      dispatch(setIsRequestProcessing(true));
      setTimeout(() => {
        dispatch(setIsRequestProcessing(false));
        renderCopilotEmailCards(copilotFlowJson[3]);
        dispatch(updateSpaceBtnCounterForCopilotEmail(4));
      }, 2000);
    }
  }, [isEmailResponseLoadedInEditor]);

  /**
   * returnAgentCopilotContentAction - retun agent copilot content action based on record type
   * @param props - suggestion
   * @example returnAgentCopilotContentAction()
   */
  const returnAgentCopilotContentAction = (recordType: string) => {
    const actionMap: { [key: string]: string } = {
      sentiment: 'sentimentAndReason',
      suggestionArticle: 'kbCombo_Voice',
      salesOffer: 'salesOffer_Voice',
      compliance: 'compliance_Voice',
      emailResponseSuggested: 'emailResponseSuggested',
      emailResponseGenerated: 'emailResponseGenerated',
      suggestionArticleWithLinks: 'kbCombo_Email',
      // Default case is handled by returning 'nbr' if recordType is not found in actionMap
    };

    return actionMap[recordType] || 'nbr';
  };

  /**
   * renderCopilotVoiceCards - render copilot cards in copilot block
   * @param props -?-suggestion
   * @example renderCopilotVoiceCards()
   */
  const renderCopilotVoiceCards = (currentCard: any) => {
    // NBR steps are no longer rendered, so skip ahead to the next renderable step
    let stepIndex = spaceBtnClickedCountForVoice;
    let suggestionsCardList = currentCard;
    while (
      suggestionsCardList &&
      returnAgentCopilotContentAction(suggestionsCardList.recordType) === 'nbr'
    ) {
      stepIndex += 1;
      suggestionsCardList = copilotFlowJson[stepIndex];
    }
    if (!suggestionsCardList) {
      dispatch(updateSpaceBtnCounterForCopilotVoice(copilotFlowJson.length));
      handleDispositionCard();
      return;
    }
    const { recordType, sentimentScore, message } = suggestionsCardList;
    const agentCopilotContentAction =
      returnAgentCopilotContentAction(recordType);
    const isSentimentAndReason =
      agentCopilotContentAction === 'sentimentAndReason';
    const contactId = `${activeContact?.id}`;
    const utcTimestamp = isSentimentAndReason
      ? new Date().toISOString()
      : '2024-04-23T04:27:23.455584Z';
    const objectIdPrefix = 'e15d4e75-39fa-4f9d-85a1-daffe6d16a40_';

    let content;
    const contentType = agentCopilotContentAction;
    let objectId = objectIdPrefix + contentType;

    if (isSentimentAndReason) {
      const sentimentLabels = ['Negative', 'Neutral', 'Positive'];
      const sentimentIndex = Math.max(
        0,
        Math.min(sentimentScore, sentimentLabels.length - 1)
      );
      const sentimentString = sentimentLabels[sentimentIndex];
      const reason = message[0].description;

      const sentimentMap: {
        [key: string]: { sentiment: string; icon: string };
      } = {
        Neutral: {
          sentiment: 'Customer Sentiment is Neutral',
          icon: 'NeutralPositiveSentimentIcon',
        },
        Positive: {
          sentiment: 'Customer Sentiment is Positive',
          icon: 'PositiveSentimentIcon',
        },
        Negative: {
          sentiment: 'Customer Sentiment is Negative',
          icon: 'NegativeSentimentIcon',
        },
      };

      const { sentiment, icon } = sentimentMap[sentimentString];
      content = JSON.stringify({ sentiment, icon, description: reason });
      objectId = objectIdPrefix + 'sentiment';
    } else {
      content =
        recordType === 'compliance'
          ? JSON.stringify({
              formattedInstructionsForAgent: `${message[0].description}`,
            })
          : recordType === 'salesOffer'
          ? JSON.stringify({ offers: message[0] })
          : JSON.stringify(message[0]);
    }

    const response = {
      agentAssistSource: 'Illuminate',
      contactId,
      utcTimestamp,
      acpAppElements: [
        {
          cardType: 'AdaptiveCard',
          contentType,
          content,
          objectId,
        },
      ],
      type: null,
    } as ApiResponse;

    processApiResponse(response, dispatch);
    dispatch(
      updateSpaceBtnCounterForCopilotVoice(stepIndex + 1)
    );
  };

  /**
   * Renders the Copilot email cards based on the provided suggestions card list.
   * @param suggestionsCardList - The list of suggestions cards.
   * @example renderCopilotEmailCards()
   */
  const renderCopilotEmailCards = (suggestionsCardList: any) => {
    const { recordType, sentimentScore, message } = suggestionsCardList;
    const agentCopilotContentAction =
      returnAgentCopilotContentAction(recordType);
    const isSentimentAndReason =
      agentCopilotContentAction === 'sentimentAndReason';
    const isNBR = agentCopilotContentAction === 'nbr';
    const isEmailResponse = agentCopilotContentAction === 'emailResponse';
    const contactId = `${activeContact?.id}`;
    const utcTimestamp = isSentimentAndReason
      ? new Date().toISOString()
      : '2024-04-23T04:27:23.455584Z';
    const objectIdPrefix = 'e15d4e75-39fa-4f9d-85a1-daffe6d16a40_';

    let content;
    const contentType = agentCopilotContentAction;
    let objectId = objectIdPrefix + contentType;

    if (!isNBR) {
      if (isSentimentAndReason) {
        const sentimentLabels = ['Negative', 'Neutral', 'Positive'];
        const sentimentIndex = Math.max(
          0,
          Math.min(sentimentScore, sentimentLabels.length - 1)
        );
        const sentimentString = sentimentLabels[sentimentIndex];
        let initialReason = message[0].description;
        if (initialReason && activeContact?.customer?.firstName) {
          const words = initialReason.split(' ');
          if (words.length > 0) {
            words[0] = activeContact.customer.firstName;
            initialReason = words.join(' ');
          }
        }
        const reason = initialReason;

        const sentimentMap: {
          [key: string]: { sentiment: string; icon: string };
        } = {
          Neutral: {
            sentiment: 'Customer Sentiment is Neutral',
            icon: 'NeutralPositiveSentimentIcon',
          },
          Positive: {
            sentiment: 'Customer Sentiment is Positive',
            icon: 'PositiveSentimentIcon',
          },
          Negative: {
            sentiment: 'Customer Sentiment is Negative',
            icon: 'NegativeSentimentIcon',
          },
        };

        const { sentiment, icon } = sentimentMap[sentimentString];
        content = JSON.stringify({ sentiment, icon, description: reason });
        objectId = objectIdPrefix + 'sentiment';
      } else {
        content =
          recordType === 'compliance'
            ? { formattedInstructionsForAgent: `${message[0].description}` }
            : recordType === 'salesOffer'
            ? JSON.stringify({ offers: message[0] })
            : JSON.stringify(message[0]);
      }

      const response = {
        agentAssistSource: 'Illuminate',
        contactId,
        utcTimestamp,
        acpAppElements: [
          {
            cardType: 'AdaptiveCard',
            contentType,
            content,
            objectId,
          },
        ],
        type: null,
      } as ApiResponse;

      processApiResponse(response, dispatch);
    }
    if (spaceBtnClickedCountForEmail <= copilotFlowJson.length) {
      dispatch(
        updateSpaceBtnCounterForCopilotEmail(spaceBtnClickedCountForEmail + 1)
      );
    }
  };

  /**
   * Function to render copilot response in copilot block
   * @param event -any
   * @example handleCopilotResponse
   */
  const handleCopilotResponse = async () => {
    dispatch(updateAppSpaceTab({ tab: AppSpace.COPILOT }));
    if (activeContact?.media === 'Voice') {
      renderCopilotVoiceCards(suggestionsCardList);
    } else if (activeContact?.channel === 'Email') {
      renderCopilotEmailCards(copilotFlowJson[spaceBtnClickedCountForEmail]);
    }
    //dispatch(setIsRequestProcessing(false));
  };

  /**
   * Function to open disposition card
   * @param event -any
   * @example handleDispositionCard
   */
  const handleDispositionCard = async () => {
    if (!isDispositionOpen) {
      await delay(1000);
      dispatch(toggleDispositionCard(true));
    }
  };

  /**
   * Function to set height of interaction post
   * @param event -any
   * @example isEmail
   */
  const delay = (ms: any) => new Promise((resolve) => setTimeout(resolve, ms));

  /**
   * Handles the space key event for the copilot functionality based on the active contact's media type.
   * If the active contact's media is 'Voice':
   * - If the space key is pressed and there are more copilot voice assist data available, and the search input is not focused,
   *   it hides the NBR block, sets the request processing state, and after a delay of 2 seconds, sets the request processing state to false
   *   and calls the handleCopilotResponse function.
   * - If there are no more copilot voice assist data available, it calls the handleDispositionCard function.
   *
   * If the active contact's media is 'Email' and channel is 'Email':
   * - If the space key is pressed and there are more copilot email assist data available, and the search input is not focused,
   *   it hides the NBR block, sets the request processing state, and after a delay of 2 seconds, sets the request processing state to false.
   *   If the space button is clicked for the second time, it sets the isCopilotEmailResponseGenerated state to true.
   *   Finally, it calls the handleCopilotResponse function.
   * - If there are no more copilot email assist data available, it calls the handleDispositionCard function.
   *
   * @param event - The key event object.
   */
  const handleKeyDown = useCallback(
    (event?: any) => {
      if (activeContact?.media === 'Voice') {
        if (
          event?.key === 'ArrowRight' &&
          spaceBtnClickedCountForVoice < copilotFlowJson.length &&
          document.getElementById('searchInput')?.id !==
            document.activeElement?.id
        ) {
          dispatch(setIsRequestProcessing(true));
          setTimeout(() => {
            dispatch(setIsRequestProcessing(false));
            handleCopilotResponse();
          }, 2000);
        } else if (copilotFlowJson.length === spaceBtnClickedCountForVoice) {
          handleDispositionCard();
        }
      } else if (
        activeContact?.media === MediaType.DIGITAL &&
        activeContact?.channel === 'Email'
      ) {
        if (
          event?.key === 'ArrowRight' &&
          spaceBtnClickedCountForEmail < copilotFlowJson.length &&
          document.getElementById('searchInput')?.id !==
            document.activeElement?.id
        ) {
          dispatch(setIsRequestProcessing(true));
          setTimeout(() => {
            dispatch(setIsRequestProcessing(false));
            if (spaceBtnClickedCountForEmail === 2) {
              dispatch(setIsCopilotEmailResponseGenerated(true));
            }
            handleCopilotResponse();
          }, 2000);
        } else if (copilotFlowJson.length === spaceBtnClickedCountForEmail) {
          handleDispositionCard();
        }
      } else if (
        event.key === 'ArrowRight' &&
        updateCopilotCardSet.includes(spaceBtnClickedCount) &&
        activeContact?.media === 'VideoCall'
      ) {
        // get next copilot response from mock data and dispatch it to update slice
        const copilotIncomingCard =
          copilotCardMockDataForVideoCall[spaceBtnClickedCount];

        if (copilotIncomingCard && activeContact?.media === 'VideoCall') {
          if (spaceBtnClickedCount === 1 || spaceBtnClickedCount === 8) {
            dispatch(
              updateCopilotCardListForVideoCall({
                data: copilotIncomingCard,
                type: 'update',
              })
            );
            dispatch(
              updateCopilotCardListForVideoCall({
                data: copilotCardMockDataForVideoCall[spaceBtnClickedCount + 1],
                type: 'update',
              })
            );
            dispatch(
              updateSpaceBtnCounterForCopilotCard(spaceBtnClickedCount + 2)
            );
          } else {
            if (spaceBtnClickedCount === 5) {
              dispatch(setIsDamagedLuggagePhotoUploaded(true));
            }
            dispatch(
              updateCopilotCardListForVideoCall({
                data: copilotIncomingCard,
                type: 'update',
              })
            );
            dispatch(
              updateSpaceBtnCounterForCopilotCard(spaceBtnClickedCount + 1)
            );
          }
        }
      } else if (
        event.key === 'ArrowRight' &&
        activeContact?.media === 'VideoCall' &&
        (spaceBtnClickedCount === 1 ||
          spaceBtnClickedCount === 4 ||
          spaceBtnClickedCount === 6)
      ) {
        let suggestionsCardListCopy: any = Object.assign(
          [],
          suggestionsVoiceCallCardList
        );
        if (
          activeContact?.media !== 'VideoCall' &&
          spaceBtnClickedCount === 6
        ) {
          dispatch(updateLoadAutoSummary(true));
        }
        suggestionsCardListCopy = suggestionsCardListCopy?.map((item: any) => {
          if (
            activeContact?.media !== 'VideoCall' &&
            item.isSelectedAsQuickReply === false
          ) {
            return { ...item, isSelectedAsQuickReply: true };
          }
          if (
            activeContact?.media === 'VideoCall' &&
            item.buttonCaption === 'Start'
          ) {
            // code to add remburishmnet claim tab
            dispatch(
              updateContactInteraction({
                id: activeContact?.id,
                interaction: getInteractionData('reimbursementClaim'),
              })
            );
            return { ...item, buttonCaption: 'Started' };
          }
          if (
            activeContact?.media === 'VideoCall' &&
            item.buttonCaption === 'Started'
          ) {
            if (spaceBtnClickedCount === 6) {
              dispatch(setIsReimbursementClaimApproved(true));
            }
            return { ...item, buttonCaption: 'Completed' };
          }
          return item;
        });
        activeContact?.media === 'VideoCall' &&
          dispatch(
            updateCopilotCardListForVideoCall({
              data: suggestionsCardListCopy,
              type: 'reset',
            })
          );

        console.log(suggestionsCardListCopy);

        dispatch(updateSpaceBtnCounterForCopilotCard(spaceBtnClickedCount + 1));
      } else if (
        event.key === 'ArrowRight' &&
        activeContact?.media === 'VideoCall' &&
        (spaceBtnClickedCount === 6 || spaceBtnClickedCount === 11) &&
        updateCopilotCardSet.length > 0
      ) {
        dispatch(updateLoadAutoSummary(true));

        dispatch(updateSpaceBtnCounterForCopilotCard(spaceBtnClickedCount + 1));
      } else if (activeContact?.media === 'VideoCall') {
        dispatch(updateSpaceBtnCounterForCopilotCard(spaceBtnClickedCount + 1));
      }
      document.getElementById('copilotBlock')?.scroll({
        top: document.getElementById('copilotBlock')?.scrollHeight, //scroll to the bottom of the element
        behavior: 'smooth', //auto, smooth, initial, inherit
      });
      dispatch(setIsSuggestionClicked(false));
    },

    [
      activeContact,
      spaceBtnClickedCountForVoice,
      spaceBtnClickedCountForEmail,
      spaceBtnClickedCount,
    ]
  );

  // Update the ref to the latest version of spaceFunction
  useEffect(() => {
    handleKeyDownRef.current = handleKeyDown;
  }, [handleKeyDown]);

  useEffect(() => {
    /**
     * @example
     *
     */
    const handleKeyDown = (event: any) => {
      if (handleKeyDownRef.current) {
        handleKeyDownRef.current(event);
      }
    };
    document.addEventListener('keydown', handleKeyDown, false);

    return () => {
      document.removeEventListener('keydown', handleKeyDown, false);
    };
  }, []);

  /**
   * Function to handle on click event for expand image
   * @param imgUrl - string
   * @param imgAltText - string
   * @example handleExpandImageClick(imgUrl, imgAltText)
   */
  const handleExpandImageClick = (imgUrl: string, imgAltText: string) => {
    setimageExpand({ url: imgUrl, altText: imgAltText });
  };

  /**
   * Function to handle on click event for actions
   * @param action - ActionMetaData
   * @param cardItem - CardData
   * @param cardId - number
   * @example handleOnClick(action, cardItem, cardId)
   */
  const handleOnClick = async (
    action: ActionMetaData,
    cardItem: CardData,
    cardId: number
  ) => {
    const caseId = activeContactId || '';
    const copilotSource = activeContact?.copilotSource;
    //const  caseId = '201251606723';
    const actionName = action.data?.name;
    if (actionName === CardActions.EXPAND_IMAGE) {
      setIsModalOpen(true);
    }
    let containerIndex = 3;
    if (actionsForOpenContainer.includes(actionName)) {
      let containerId;

      switch (actionName) {
        case CardActions.OPEN_INTERNAL_USE_CONTAINER:
          containerId = ContainerId.KB_INTERNAL_USE;
          containerIndex = 1;
          break;
        case CardActions.OPEN_LINKS_CONTAINER:
          containerId = ContainerId.KB_LINKS;
          containerIndex = 2;
          break;
        case CardActions.OPEN_IMAGES_CONTAINER:
          containerId = ContainerId.KB_IMAGES;
          containerIndex = 3;
          break;
        case CardActions.OPEN_PROCESS_STEPS_CONTAINER:
          containerId = ContainerId.KB_PROCESS_STEPS;
          containerIndex = 4;
          break;
        default:
          break;
      }

      containerId &&
        CcfAgentCopilotContainerFunctions.handleContainerOpen(
          cardItem.body[containerIndex],
          cardId,
          containerId,
          caseId,
          dispatch
        );
    }

    if (actionName === CardActions.OPEN_WORKFLOW_CONTAINER) {
      const workflowQuestionsContainer =
        CcfAgentCopilotContainerFunctions.getContainerById(
          cardItem.body,
          ContainerId.WORKFLOW_QUESTIONS_CONTAINER
        );
      await CcfAgentCopilotContainerFunctions.handleContainerOpen(
        workflowQuestionsContainer,
        cardId,
        ContainerId.WORKFLOW_QUESTIONS_CONTAINER,
        caseId,
        dispatch
      );
      const workflowButtonsContainer =
        CcfAgentCopilotContainerFunctions.getContainerById(
          cardItem.body,
          ContainerId.WORKFLOW_BUTTONS_CONTAINER
        );
      await CcfAgentCopilotContainerFunctions.handleContainerOpen(
        workflowButtonsContainer,
        cardId,
        ContainerId.WORKFLOW_BUTTONS_CONTAINER,
        caseId,
        dispatch
      );
    }
    if (actionName === CardActions.GENERATE_EMAIL_RESPONSE) {
      await handleKeyDown({ key: 'ArrowRight' });
    }
    if (actionName === CardActions.SUBMIT_INTENTS) {
      await handleSubmitIntentMessages(
        caseId,
        cardId,
        action.data?.text,
        dispatch,
        translate
      );
    }
    if (actionName !== CardActions.HANDLE_INTENTS) {
      await CcfAgentCopilotContainerFunctions.handleClickActions(
        {
          action,
          cardItem,
          caseId,
          isSmView,
          cardId,
          activeInteractionID,
          copilotSource,
        },
        CcfAgentCopilotContainerFunctions.handleIconChange,
        handleExpandImageClick,
        dispatch,
        ''
      );
    }
    if (actionName === CardActions.HANDLE_INTENTS) {
      // Use the intent of the card that was actually clicked (matched by
      // title), not the shared activeContact.intents which only holds whichever
      // Task Assist was rendered last — otherwise auto-running Task Assist 1
      // would play Task Assist 2's steps.
      const clickedTitle = action.data?.text;
      const clickedIntent =
        copilotFlowJson?.find(
          (item: any) =>
            item?.recordType === 'intentAndAssistance' &&
            item?.message?.[0]?.title === clickedTitle
        )?.message ?? intents;

      await CcfAgentCopilotContainerFunctions.handleInsertingIntentMessages(
        caseId,
        clickedIntent,
        userReplyChatData,
        loginCredentialsArray[0],
        activeContact?.customer.firstName,
        lastindexVal,
        activeInteractionID,
        dispatch
      );
      const taskInterval =
        clickedIntent[0]?.tasks[0]?.buttons?.[0]?.time ?? 3000;
      const orderLength = clickedIntent[0].tasks?.length * 2;
      const interval = taskInterval * orderLength;
      setTimeout(() => {
        handleSubmitIntentMessages(
          caseId,
          cardId,
          clickedIntent[0].title,
          dispatch,
          translate
        );
      }, interval);
    }
  };

  /**
   * Function to close modal
   * @example closeModal()
   */
  const closeModal = () => {
    setIsModalOpen(false);
  };

  useEffect(() => {
    // Scroll to the bottom of the container when new cards are added
    if (containerRef.current) {
      setTimeout(() => {
        containerRef?.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'end',
        });
      }, 0);
      //dispatch(setIsRequestProcessing(false));
    }
    // if(existingAdaptiveCards[existingAdaptiveCards.length - 1]?.contentType === 'intentSuccess') {
    //   dispatch(updateFlowMsgIndex(flowMsgIndex + 1));
    // }
  }, [existingAdaptiveCards.length]);

  return isJourneySummaryExpanded ? (
    <Box
      sx={
        isChannelVoice
          ? cardStyles.journeySummaryContainerVoice
          : cardStyles.journeySummaryContainer
      }
    >
      <CcfBox sx={cardStyles.cardsContainer}>
        {journeySummaryCard.map((card, cardId) => {
          const template = new ACData.Template(card.content);
          const copilotCard = template.expand(context);
          return (
            <AdaptiveCard
              key={`ad-${cardId}`}
              style={{ fontFamily: theme.typography.fontFamily }}
              payload={copilotCard}
              hostConfig={hostConfig}
              onExecuteAction={(action: any) =>
                handleOnClick(action, copilotCard, cardId)
              }
            />
          );
        })}
      </CcfBox>
    </Box>
  ) : (
    <>
      <Box sx={cardStyles.copilotContainer}>
        {rtSummary && (
          <CcfAgentCopilotRunningSummary summary={rtSummaryContent.summary} />
        )}
        {!loadAutoSummary && activeContact?.media === MediaType.VIDEOCALL && (
          <CcfBox
            id="agent_videocall_copilotBlock"
            className={styles.coPilotCardsBlock}
            style={{
              display: 'block',
            }}
          >
            {suggestionsVoiceCallCardList &&
              suggestionsVoiceCallCardList.length > 0 &&
              activeContact?.media === 'VideoCall' && (
                <>
                  <CcfBox
                    id="agent_videocall_copilot_landed 60 minutes ago"
                    className={styles.suggestionCard}
                  >
                    <CcfCopilotSuggestionIcon
                      className={styles.suggestionIcon}
                    />
                    <CcfTypography className={styles.suggestionTxt}>
                      landed 60 minutes ago
                    </CcfTypography>
                  </CcfBox>
                  <CcfCopilotVideoCallCard
                    suggestionsCardList={suggestionsVoiceCallCardList}
                  />
                </>
              )}
          </CcfBox>
        )}
        {loadAutoSummary &&
          activeContact?.media === MediaType.VIDEOCALL &&
          (showSummaryLoadingAnimation ? (
            <CcfBox
              style={{ height: '100%', width: '100%' }}
              display={'flex'}
              justifyContent={'center'}
              alignItems={'center'}
              id="agent_videocall_copilot_summaryLoadingAnimation"
            >
              <CcfAutosummaryAnimationIcon
                id="agent_videocall_copilot_summaryLoadingIcon"
                className={styles.isLoading}
              />
            </CcfBox>
          ) : (
            // eslint-disable-next-line react/jsx-no-undef
            <CcfCopilotAutoSummary
              copilotAutoSummary={damagedLuggageAutoSummaryData}
            />
          ))}

        {activeContact?.media !== MediaType.VIDEOCALL && (
          <CcfBox sx={cardStyles.cardsContainer}>
            {existingAdaptiveCards.length === 0 ? (
              <CcfEmptyCopilotState />
            ) : (
              <CcfCard sx={cardStyles.copilotCard} data-testid="list-item">
                <div ref={containerRef}>
                  {existingAdaptiveCards.map((card, cardId) => {
                    let updatedCopilotCard = { ...card.content };
                    try {
                      updatedCopilotCard = JSON.stringify(card.content).replace(
                        /adp_\w+/g,
                        (match) => {
                          const value = translate(match);
                          return value ? value : match;
                        }
                      );
                      updatedCopilotCard = JSON.parse(updatedCopilotCard);
                    } catch (e) {
                      console.log('[error] ------->', e);
                    }
                    const template = new ACData.Template(updatedCopilotCard);
                    const copilotCard = template.expand(context);
                    return (
                      <Box
                        sx={
                          isChannelVoice
                            ? cardStyles.copilotContentVoice
                            : cardStyles.copilotContent
                        }
                        key={cardId}
                        id={`copilotCard-${cardId}`}
                      >
                        <AdaptiveCard
                          key={`ad-${cardId}`}
                          style={{ fontFamily: theme.typography.fontFamily }}
                          payload={copilotCard}
                          hostConfig={hostConfig}
                          onExecuteAction={(action: any) =>
                            handleOnClick(action, copilotCard, cardId)
                          }
                        />
                        <CcfImageWithZoom
                          isModalOpen={isModalOpen}
                          closeModal={closeModal}
                          isInboxCollapsed={isInboxCollapsed}
                          imgUrl={imageExpand.url}
                          imgAltText={imageExpand.altText}
                        />
                      </Box>
                    );
                  })}
                </div>
              </CcfCard>
            )}
          </CcfBox>
        )}
        {isProcessAutomationEnabled(activeContact) && (
          <CcfProcessAutomationPanel contact={activeContact} />
        )}
        {isResponseLoading && (
          <CcfBox style={cardStyles.copilotInteractionAnimation}>
            <CcfDancingDotsIcon style={cardStyles.dancingDotsIcon} />
          </CcfBox>
        )}
      </Box>
      {<CcfAgentCopilotSearchContainer />}
    </>
  );
}

export default CcfScriptedCopilot;
